import React, { useState, useCallback } from 'react';
import {
  Box,
  Drawer,
  TextField,
  Typography,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Checkbox,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import type { ListChildComponentProps } from 'react-window';
import { FixedSizeList } from 'react-window';
import debounce from 'lodash/debounce';
import { styled } from '@mui/material/styles';
import GradientButton from '../MUICustomButton/GradientButton';
import OutlineButton from '../MUICustomButton/OutlineButton';

interface Filter {
  _fieldName: string;
  _appliedValues: { _formattedValue: string }[];
  _isAllSelected: boolean;
}

interface FilterDrawerProps {
  isFilterDrawerOpen: boolean;
  onClose: () => void;
  filters: Filter[];
  filterValues: Record<string, string[]>;
  filterSearch: string;
  setFilterSearch: (value: string) => void;
  handleFilterChange: (filterName: string, selectedValues: string[]) => void;
  applyFilters: () => Promise<void>;
  clearFilters: () => Promise<void>;
}

const CustomScrollbarBox = styled(Box)`
  &.custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #ed4b52 #f5f5f5;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f5f5f5;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #ed4b52;
      border-radius: 4px;
      border: 2px solid #f5f5f5;
    }
  }
`;

const CustomAccordionSummary = styled(AccordionSummary)`
  &.accordion-summary {
    background-color: #f5f5f5;
    border-bottom: 1px solid #ddd;

    &:hover {
      background-color: #eaeaea;
    }
  }

  & .MuiAccordionSummary-content.Mui-expanded {
    margin: 0px;
  }
     &.Mui-expanded {
     background-color: #e0e0e0;
    min-height: 50px !important; /* Remove default min-height */
  }

`;

const FilterDrawer: React.FC<FilterDrawerProps> = ({
  isFilterDrawerOpen,
  onClose,
  filters,
  filterValues,
  filterSearch,
  setFilterSearch,
  handleFilterChange,
  applyFilters,
  clearFilters,
}) => {
  const [expandedPanel, setExpandedPanel] = useState<string | false>(false);

  const handleAccordionChange =
    (panel: string) =>
    (_: React.SyntheticEvent, isExpanded: boolean): void => {
      setExpandedPanel(isExpanded ? panel : false);
    };

  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#e0e0e0',
        borderRadius: '8px',
      },
      '&:hover fieldset': {
        borderColor: '#bdbdbd',
      },
    },
    '& .MuiInputBase-input': {
      fontSize: 14,
    },
    '& .MuiInputLabel-root': {
      fontSize: 14,
    },
  };

  const renderFilter = (filter: Filter): React.ReactNode => {
    const selectedCount = filterValues[filter._fieldName]?.length ?? 0;
    const options = filter._appliedValues.map(
      (option) => option._formattedValue,
    );

    return (
      <Accordion
        key={filter._fieldName}
        expanded={expandedPanel === filter._fieldName}
        onChange={handleAccordionChange(filter._fieldName)}
        sx={{
          boxShadow: 'none',
          '&:not(:last-child)': {
            borderBottom: '1px solid #ddd',
          },
          '&.Mui-expanded': {
            margin: 0,
          },
          backgroundColor: 'white',
          borderRadius: 2,
        }}
      >
        <CustomAccordionSummary
          expandIcon={<ArrowDropDownIcon />}
          aria-controls={`${filter._fieldName}-content`}
          id={`${filter._fieldName}-header`}
          className="accordion-summary"
        >
          <Typography sx={{ flexGrow: 1, fontSize: 14, fontWeight: 'normal' }}>
            {filter._fieldName}
          </Typography>
          {selectedCount > 0 && (
            <Chip
              label={selectedCount}
              size="small"
              color="primary"
              sx={{ mr: 1 }}
            />
          )}
        </CustomAccordionSummary>
        <AccordionDetails sx={{ mt: '10px', borderTop: 'none' }}>
          <Autocomplete
            multiple
            fullWidth
            disableCloseOnSelect
            options={options}
            value={filterValues[filter._fieldName] ?? []}
            onChange={(_, newValue) =>
              handleFilterChange(filter._fieldName, newValue)
            }
            renderInput={(params) => (
              
              <TextField
                {...params}
                variant="outlined"
                label="Select values"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#757575', pl:'5px' }} />
                    </InputAdornment>
                  ),
                }}
                sx={textFieldStyles}
              />
            )}
            ListboxComponent={ListboxComponent}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox style={{ marginRight: 8 }} checked={selected} />
                {option}
              </li>
            )}
          />
        </AccordionDetails>
      </Accordion>
    );
  };

  const filteredFilters = filters.filter((filter) =>
    filter._fieldName.toLowerCase().includes(filterSearch.toLowerCase()),
  );

  const debouncedSetFilterSearch = useCallback(
    debounce((value: string) => setFilterSearch(value), 300),
    [setFilterSearch],
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    debouncedSetFilterSearch(e.target.value);
  };

  const handleApplyFilters = useCallback(() => {
    onClose();
    void applyFilters();
  }, [onClose, applyFilters]);

  const handleClearFilters = useCallback(() => {
    void clearFilters();
    onClose();
  }, [clearFilters, onClose]);

  return (
    <Drawer
      anchor="right"
      open={isFilterDrawerOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 360,
          height: '100%',
        },
      }}
    >
      <CustomScrollbarBox
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#ffffff',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          fontFamily: 'Manrope, sans-serif',
          fontSize: 14,
        }}
        className="custom-scrollbar"
        role="presentation"
      >
        <Box sx={{ padding: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 2,
            }}
          >
            <Typography
              variant="h6"
              id="filter-drawer-title"
              sx={{ fontWeight: 'normal', fontSize: '1.5rem' }}
            >
              Filters
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, ml: 'auto', mt: 1 }}>
              <Box
                sx={{ height: '30px', display: 'flex', alignItems: 'center' }}
              >
                <GradientButton color="red" onClick={handleApplyFilters}>
                  Apply Filter
                </GradientButton>
              </Box>
              <Box
                sx={{ height: '30px', display: 'flex', alignItems: 'center' }}
              >
                <OutlineButton
                  color="gray"
                  onClick={handleClearFilters}
                  width="120px"
                >
                  Clear
                </OutlineButton>
              </Box>
            </Box>
          </Box>
          <TextField
            fullWidth
            label="Search Filters"
            variant="outlined"
            value={filterSearch}
            onChange={handleSearchChange}
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#757575' }}/>
                </InputAdornment>
              ),
              sx: { fontSize: 14 },
            }}
            sx={textFieldStyles}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
           
          }}
          className="custom-scrollbar"
        >
          {filteredFilters.length > 0 ? (
            filteredFilters.map((filter) => renderFilter(filter))
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: 14,
                fontWeight: 'normal',
              }}
            >
              No matching filters found.
            </Typography>
          )}
        </Box>
      </CustomScrollbarBox>
    </Drawer>
  );
};

const LISTBOX_PADDING = 8;

function renderRow(props: ListChildComponentProps): React.ReactElement {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { data, index, style } = props;
  const dataSet = data as React.ReactElement[];
  return React.cloneElement(dataSet[index], {
    style: {
      ...style,
      top: (style.top as number) + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

OuterElementType.displayName = 'OuterElementType';

function useResetCache(
  data: React.ReactElement[],
): React.RefObject<FixedSizeList> {
  const ref = React.useRef<FixedSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.scrollToItem(0, 'start');
    }
  }, [data]);
  return ref;
}

const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children) as React.ReactElement[];
  const itemCount = itemData.length;
  const itemSize = 48;

  const getHeight = (): number => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.length * itemSize;
  };

  const gridRef = useResetCache(itemData);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <FixedSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={itemSize}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </FixedSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

ListboxComponent.displayName = 'ListboxComponent';

export default FilterDrawer;
